import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { HOME_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { previewInvite, joinViaInviteCode } from '@/api/groups';
import { GROUPS_QUERY_KEY } from '@/hooks/queries/groups';
import { useUiSignals } from '@/stores/uiSignals';
import { toast } from '@/stores/toast';
import { PrimaryButton } from '@/components/Buttons';
import { useState } from 'react';
import { RacoreText } from '@/components/Logos';

/** Invite preview + join (wireframe 3c). Reached via a shared /invite/:code link. */
export const Route = createFileRoute('/invite/$code')({
  component: InviteScreen,
});

function InviteScreen() {
  const { code } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [joining, setJoining] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invite', code],
    queryFn: () => previewInvite(code),
    retry: false,
  });

  const join = async () => {
    setJoining(true);
    try {
      const { groupId, pending } = await joinViaInviteCode(code);
      await qc.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      if (pending) {
        toast.success(t('groups.join.requestSent'));
      } else {
        useUiSignals.getState().setLandOnCircle(groupId);
        toast.success(t('groups.join.success'));
      }
      navigate({ to: HOME_ROUTE, replace: true });
    } catch {
      toast.error(t('groups.join.error'));
      setJoining(false);
    }
  };

  return (
    <div
      className="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center tw:px-8 tw:text-center"
      data-testid="screen-invite"
    >
      <div className="tw:mb-10">
        <RacoreText width={140} height={30} />
      </div>

      {isLoading ? (
        <span className="tw:size-8 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
      ) : isError || !data ? (
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-6">
          <p className="tw:text-base tw:text-grey-2">{t('groups.join.error')}</p>
          <button
            type="button"
            onClick={() => navigate({ to: HOME_ROUTE, replace: true })}
            className="tw:text-[15px] tw:font-semibold tw:text-blue-primary"
          >
            {t('groups.join.backHome')}
          </button>
        </div>
      ) : (
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
          <p className="tw:text-base tw:text-grey-2">{t('groups.invite.youAreInvited')}</p>
          <span className="tw:flex tw:size-24 tw:items-center tw:justify-center tw:rounded-3xl tw:bg-dark-1 tw:text-[52px]">
            {data.group.emoji || data.group.name.charAt(0).toUpperCase()}
          </span>
          <h1 className="tw:heading-2 tw:text-white" data-testid="invite-group-name">
            {data.group.name}
          </h1>
          <p className="tw:caption tw:text-grey-3">
            {t('groups.invite.memberCount', { count: data.group.memberCount })}
          </p>

          <div className="tw:mt-6 tw:w-full tw:max-w-[280px]">
            <PrimaryButton
              label={t('groups.join.button')}
              onClick={join}
              loading={joining}
              data-testid="btn-invite-join"
            />
          </div>
        </div>
      )}
    </div>
  );
}
